import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  ImagePlus,
  MessageSquareQuote,
  Send,
  ShieldCheck,
  Star,
  UploadCloud,
  Video,
  X,
} from "lucide-react";
import { AppDispatch } from "../../../store";
import { useAppSelector } from "../../../Redux/hooks";
import {
  fetchTestimonialById,
  updateTestimonial,
} from "../../../Redux/custmorTestimonials/slice";
import { updateTestimonialDto } from "../../../Redux/custmorTestimonials/types";
import { uploadToCloudinary } from "../../../utility/uploadToCloudinary";
import "./CreateTestimonialPage.css";

const cleanUrl = (url?: string | null) =>
  url?.replace(/^https?:\/\/localhost:\d+/, "") || "";

const UpdateTestimonial = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isLoading, currentTestimonial } = useAppSelector(
    (state) => state.testimonials,
  );
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      dispatch(fetchTestimonialById(id));
    }
  }, [id, dispatch]);

  const [form, setForm] = useState<updateTestimonialDto>({
    title: "",
    content: "",
    rating: 0,
    image: "",
    video: "",
  } as updateTestimonialDto);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoName, setVideoName] = useState("");
  const [hoveredRating, setHoveredRating] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (currentTestimonial) {
      setForm({
        title: currentTestimonial.title,
        content: currentTestimonial.content,
        rating: currentTestimonial.rating,
        image: currentTestimonial.image || "",
        video: currentTestimonial.video || "",
      } as updateTestimonialDto);
      if (currentTestimonial.image) {
        setImagePreview(cleanUrl(currentTestimonial.image));
      }
      if (currentTestimonial.video) {
        setVideoName("Existing video attached");
      }
    }
  }, [currentTestimonial]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPreviewOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const displayRating = hoveredRating || Number(form.rating || 0);

  // TEXT + NUMBER HANDLER
  const handleTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "title") {
      if (value.length > 25) return;
      if (value !== "" && !/^[a-zA-Z0-9&, ]*$/.test(value)) return;
      if (value.includes("  ")) return;
    }

    if (name === "content") {
      if (value.length > 250) return;
      if (value !== "" && !/^[a-zA-Z0-9&,.' @]*$/.test(value)) return;
      if (value.includes("  ")) return;
    }

    setForm((prev: any) => ({ ...prev, [name]: value }));
  };

  // FILE HANDLER
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    const file = e.target.files?.[0];

    if (!file) return;

    setFieldErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "image") {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        setFieldErrors((prev) => ({
          ...prev,
          image: "Only PNG, JPG, and JPEG formats are allowed.",
        }));
        e.target.value = "";
        return;
      }
      if (file.size > 500 * 1024) {
        setFieldErrors((prev) => ({
          ...prev,
          image: "Image size must be less than 500KB.",
        }));
        e.target.value = "";
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }

    if (name === "video") {
      if (file.type !== "video/mp4") {
        setFieldErrors((prev) => ({
          ...prev,
          video: "Only MP4 format is allowed.",
        }));
        e.target.value = "";
        return;
      }
      setVideoFile(file);
      setVideoName(file.name);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
    setForm((prev: any) => ({ ...prev, image: "" }));
  };

  const removeVideo = () => {
    setVideoFile(null);
    setVideoName("");
    setForm((prev: any) => ({ ...prev, video: "" }));
  };

  // SUBMIT
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (fieldErrors.image || fieldErrors.video) {
      setError("Please fix the file upload errors before submitting.");
      return;
    }

    if (!form.title || !form.content || form.rating === undefined) {
      setError("Please fill all required fields");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      let imageUrl = form.image || "";
      let videoUrl = form.video || "";

      if (imageFile) {
        imageUrl = await uploadToCloudinary(imageFile);
      }
      if (videoFile) {
        videoUrl = await uploadToCloudinary(videoFile);
      }

      const payload: updateTestimonialDto = {
        title: form.title,
        content: form.content,
        rating: Number(form.rating),
        image: imageUrl,
        video: videoUrl,
      };

      await dispatch(
        updateTestimonial({ id: id as string, testimonial: payload }),
      )
        .unwrap()
        .then(() => {
          setIsUploading(false);
          setSuccess("Testimonial updated successfully!");
          setTimeout(() => {
            setSuccess(null);
            navigate("/UserTestimonials");
          }, 2000);
        })
        .catch((err) => {
          setIsUploading(false);
          setError(err.message || "Error updating testimonial");
          setTimeout(() => setError(null), 3000);
        });
    } catch (err: any) {
      setIsUploading(false);
      setError(err.message || "Error uploading media");
      setTimeout(() => setError(null), 3000);
    }
  };

  return (
    <section className="create-testimonial-page">
      <div className="create-testimonial-shell">
        <header className="create-testimonial-header">
          <div className="create-testimonial-header-left">
            <button
              type="button"
              className="create-testimonial-back"
              onClick={() => navigate("/UserTestimonials")}
              aria-label="Back to testimonials"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="create-testimonial-header-icon">
              <MessageSquareQuote size={22} strokeWidth={2} />
            </div>
            <div>
              <span className="create-testimonial-eyebrow">
                SHARE YOUR EXPERIENCE
              </span>
              <h1>Update Testimonial</h1>
              <p>
                Edit your experience, rating and media for this testimonial.
              </p>
            </div>
          </div>
          <div className="create-testimonial-secure">
            <ShieldCheck size={15} />
            Your changes are linked to your Salar account
          </div>
        </header>

        <form className="create-testimonial-layout" onSubmit={handleSubmit}>
          <main className="create-testimonial-card">
            <div className="create-testimonial-card-head">
              <div>
                <span>TESTIMONIAL DETAILS</span>
                <h2>Edit your testimonial</h2>
              </div>
              <div className="create-testimonial-card-icon">
                <Send size={18} />
              </div>
            </div>

            <div className="create-testimonial-fields">
              <label className="create-testimonial-field">
                <div className="create-testimonial-label-row">
                  <span>Title</span>
                  <small>{form.title?.length || 0}/25</small>
                </div>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  maxLength={25}
                  onChange={handleTextChange}
                  placeholder="Give your testimonial a short title"
                />
              </label>

              <label className="create-testimonial-field">
                <div className="create-testimonial-label-row">
                  <span>Message</span>
                  <small>{form.content?.length || 0}/250</small>
                </div>
                <textarea
                  name="content"
                  value={form.content}
                  maxLength={250}
                  onChange={handleTextChange}
                  placeholder="Enter your message here..."
                />
              </label>

              <fieldset className="create-testimonial-rating-field">
                <legend>Rating</legend>
                <div className="create-testimonial-rating-row">
                  <div
                    className="create-testimonial-stars"
                    onMouseLeave={() => setHoveredRating(0)}
                  >
                    {Array.from({ length: 5 }, (_, index) => {
                      const value = index + 1;
                      const active = value <= displayRating;
                      return (
                        <button
                          key={value}
                          type="button"
                          className={active ? "is-active" : ""}
                          onMouseEnter={() => setHoveredRating(value)}
                          onFocus={() => setHoveredRating(value)}
                          onBlur={() => setHoveredRating(0)}
                          onClick={() =>
                            setForm((prev: any) => ({ ...prev, rating: value }))
                          }
                          aria-label={`Rate ${value} out of 5`}
                        >
                          <Star
                            size={30}
                            strokeWidth={1.8}
                            fill={active ? "currentColor" : "none"}
                          />
                        </button>
                      );
                    })}
                  </div>
                  <div className="create-testimonial-rating-copy">
                    <strong>
                      {form.rating ? `${form.rating}/5` : "Select a rating"}
                    </strong>
                    <span>Tap a star to rate your experience</span>
                  </div>
                </div>
              </fieldset>

              <div className="create-testimonial-upload-section">
                <div className="create-testimonial-section-title">
                  <div>
                    <ImagePlus size={17} />
                    <span>Upload Image</span>
                  </div>
                  <small>JPG or PNG · Max 500 KB</small>
                </div>

                {!imagePreview ? (
                  <label className="create-testimonial-dropzone">
                    <UploadCloud size={27} strokeWidth={1.8} />
                    <strong>Choose testimonial image</strong>
                    <span>Click to browse from your device</span>
                    <input
                      type="file"
                      name="image"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={handleFileChange}
                    />
                  </label>
                ) : (
                  <div className="create-testimonial-image-preview">
                    <img src={imagePreview} alt="Selected testimonial" />
                    <div className="create-testimonial-image-overlay">
                      <button
                        type="button"
                        onClick={() => setPreviewOpen(true)}
                      >
                        <Eye size={17} />
                        Preview
                      </button>
                    </div>
                    <button
                      type="button"
                      className="create-testimonial-remove-image"
                      onClick={removeImage}
                      aria-label="Remove image"
                    >
                      <X size={16} />
                    </button>
                    <span className="create-testimonial-image-name">
                      {imageFile?.name || "Current image"}
                    </span>
                  </div>
                )}
                {fieldErrors.image && (
                  <div
                    className="create-testimonial-message is-error"
                    role="alert"
                  >
                    {fieldErrors.image}
                  </div>
                )}
              </div>

              <div className="create-testimonial-upload-section">
                <div className="create-testimonial-section-title">
                  <div>
                    <Video size={17} />
                    <span>Upload Video (optional)</span>
                  </div>
                  <small>MP4 format only</small>
                </div>

                {!videoFile && !videoName ? (
                  <label className="create-testimonial-dropzone">
                    <UploadCloud size={27} strokeWidth={1.8} />
                    <strong>Choose testimonial video</strong>
                    <span>Click to browse MP4 from your device</span>
                    <input
                      type="file"
                      name="video"
                      accept="video/mp4"
                      onChange={handleFileChange}
                    />
                  </label>
                ) : (
                  <div className="create-testimonial-image-preview">
                    <div className="create-testimonial-preview-placeholder">
                      <Video size={26} />
                      <span>{videoFile?.name || videoName}</span>
                    </div>
                    <button
                      type="button"
                      className="create-testimonial-remove-image"
                      onClick={removeVideo}
                      aria-label="Remove video"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                {fieldErrors.video && (
                  <div
                    className="create-testimonial-message is-error"
                    role="alert"
                  >
                    {fieldErrors.video}
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="create-testimonial-message is-error" role="alert">
                {error}
              </div>
            )}
            {success && (
              <div
                className="create-testimonial-message is-success"
                role="status"
              >
                <CheckCircle2 size={16} />
                {success}
              </div>
            )}

            <button
              type="submit"
              className="create-testimonial-submit"
              disabled={isLoading || isUploading}
            >
              <Send size={17} />
              {isUploading
                ? "Uploading Media..."
                : isLoading
                  ? "Submitting..."
                  : "Update Testimonial"}
            </button>
          </main>

          <aside className="create-testimonial-side">
            <div className="create-testimonial-side-icon">
              <Video size={21} />
            </div>
            <span>LIVE PREVIEW</span>
            <h3>Your testimonial</h3>
            <div className="create-testimonial-preview-card">
              <div className="create-testimonial-preview-media">
                {imagePreview ? (
                  <img src={imagePreview} alt="Testimonial preview" />
                ) : (
                  <div className="create-testimonial-preview-placeholder">
                    <ImagePlus size={28} />
                    <span>Image preview</span>
                  </div>
                )}
              </div>
              <div className="create-testimonial-preview-body">
                <h4>{form.title?.trim() || "Your testimonial title"}</h4>
                <div className="create-testimonial-preview-stars">
                  {Array.from({ length: 5 }, (_, index) => (
                    <Star
                      key={index}
                      size={14}
                      fill={
                        index < Number(form.rating || 0)
                          ? "currentColor"
                          : "none"
                      }
                      className={
                        index < Number(form.rating || 0) ? "is-active" : ""
                      }
                    />
                  ))}
                </div>
                <p>
                  {form.content?.trim() ||
                    "Your testimonial message will appear here while you type."}
                </p>
              </div>
            </div>
            <div className="create-testimonial-side-note">
              <ShieldCheck size={15} />
              Review your text, rating and uploaded image before updating.
            </div>
          </aside>
        </form>
      </div>

      {previewOpen && imagePreview && (
        <div
          className="create-testimonial-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setPreviewOpen(false);
            }
          }}
        >
          <div className="create-testimonial-modal-card">
            <button
              type="button"
              className="create-testimonial-modal-close"
              onClick={() => setPreviewOpen(false)}
              aria-label="Close preview"
            >
              <X size={20} />
            </button>
            <img src={imagePreview} alt="Full testimonial preview" />
          </div>
        </div>
      )}
    </section>
  );
};

export default UpdateTestimonial;
